import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 — attempt token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true
      const refresh = localStorage.getItem('refresh_token')
      if (refresh) {
        try {
          const res = await axios.post(`${API_BASE}/token/refresh`, { refresh })
          const { access } = res.data
          localStorage.setItem('access_token', access)
          original.headers.Authorization = `Bearer ${access}`
          return api(original)
        } catch {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/login'
        }
      } else {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default api

// --- Auth ---
export const authApi = {
  register: (data: { email: string; password: string; full_name: string }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/token/pair', data),
  me: () => api.get('/auth/me'),
}

// --- LedgerPro API ---
const LP = '/ledgerpro'

export const firmApi = {
  list: () => api.get(`/dashboard-ledger/organizations`),
  create: (data: { name: string; timezone?: string; currency_code?: string }) =>
    api.post(`/dashboard-ledger/organizations`, data),
  update: (id: string, data: { name?: string; legal_name?: string; timezone?: string; currency_code?: string }) =>
    api.patch(`/dashboard-ledger/organizations/${id}`, data),
}

export const clientApi = {
  list: (firmId?: string) =>
    api.get(`${LP}/clients/`, { params: firmId ? { firm_id: firmId } : {} }),
  create: (data: Record<string, unknown>) => api.post(`${LP}/clients/`, data),
  get: (id: string) => api.get(`${LP}/clients/${id}/`),
  update: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/clients/${id}/`, data),
  archive: (id: string) => api.post(`${LP}/clients/${id}/archive/`),
  restore: (id: string) => api.post(`${LP}/clients/${id}/restore/`),
  inviteViewer: (id: string, email: string) =>
    api.post(`${LP}/clients/${id}/invite-viewer/`, null, { params: { email } }),
  listViewers: (id: string) => api.get(`${LP}/clients/${id}/viewers/`),
  removeViewer: (id: string, userId: string) =>
    api.delete(`${LP}/clients/${id}/viewers/${userId}/`),
}

export const accountApi = {
  list: (clientId: string, includeArchived = false) =>
    api.get(`${LP}/accounts/`, { params: { client_ledger_id: clientId, include_archived: includeArchived } }),
  create: (data: Record<string, unknown>) => api.post(`${LP}/accounts/`, data),
  update: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/accounts/${id}/`, data),
  archive: (id: string) => api.post(`${LP}/accounts/${id}/archive/`),
  listTemplates: () => api.get(`${LP}/accounts/templates/`),
  applyTemplate: (data: { client_ledger_id: string; template_key: string }) =>
    api.post(`${LP}/accounts/apply-template/`, data),
}

export const bankApi = {
  list: (clientId: string) =>
    api.get(`${LP}/bank-accounts/`, { params: { client_ledger_id: clientId } }),
  create: (data: Record<string, unknown>) =>
    api.post(`${LP}/bank-accounts/`, data),
  update: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/bank-accounts/${id}/`, data),
  importStatement: (bankAccountId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    // null removes the default 'application/json' Content-Type so the browser
    // can auto-set 'multipart/form-data; boundary=...' for FormData
    return api.post(`${LP}/bank-accounts/${bankAccountId}/import/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  // Xero-style import wizard endpoints
  parseCsv: (bankAccountId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`${LP}/bank-accounts/${bankAccountId}/parse-csv/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  validateImport: (bankAccountId: string, file: File, mapping: Record<string, string>, dateFormat: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mapping', JSON.stringify(mapping))
    formData.append('date_format', dateFormat)
    return api.post(`${LP}/bank-accounts/${bankAccountId}/validate-import/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  importMapped: (bankAccountId: string, file: File, mapping: Record<string, string>, dateFormat: string, excludedRows: number[]) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mapping', JSON.stringify(mapping))
    formData.append('date_format', dateFormat)
    formData.append('excluded_rows', JSON.stringify(excludedRows))
    formData.append('save_mapping', 'true')
    return api.post(`${LP}/bank-accounts/${bankAccountId}/import-mapped/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  downloadTemplate: (bankAccountId: string) =>
    api.get(`${LP}/bank-accounts/${bankAccountId}/download-template/`, { responseType: 'blob' }),
  listLines: (bankAccountId: string, status?: string) =>
    api.get(`${LP}/bank-accounts/${bankAccountId}/lines/`, { params: status ? { status } : {} }),
  reconciliationSummary: (bankAccountId: string) =>
    api.get(`${LP}/bank-accounts/${bankAccountId}/reconciliation-summary/`),
  reconcileLine: (lineId: string, data: { account_id: string; description?: string }) =>
    api.post(`${LP}/bank-accounts/lines/${lineId}/reconcile/`, data),
  createTransactionFromLine: (lineId: string, data: { account_id: string; description?: string }) =>
    api.post(`${LP}/bank-accounts/lines/${lineId}/create-transaction/`, data),
  excludeLine: (lineId: string) =>
    api.post(`${LP}/bank-accounts/lines/${lineId}/exclude/`),
  getSuggestions: (lineId: string) =>
    api.get(`${LP}/bank-accounts/lines/${lineId}/suggestions/`),
  matchTransaction: (lineId: string, transactionId: string) =>
    api.post(`${LP}/bank-accounts/lines/${lineId}/match-transaction/`, { transaction_id: transactionId }),
  approveLine: (lineId: string) =>
    api.post(`${LP}/bank-accounts/lines/${lineId}/approve/`),
  bulkApprove: (bankAccountId: string) =>
    api.post(`${LP}/bank-accounts/${bankAccountId}/bulk-approve/`),
  attachFile: (lineId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`${LP}/bank-accounts/lines/${lineId}/attach/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  detachFile: (lineId: string) =>
    api.delete(`${LP}/bank-accounts/lines/${lineId}/attach/`),
  listRules: (clientId: string) =>
    api.get(`${LP}/bank-accounts/rules/`, { params: { client_ledger_id: clientId } }),
  createRule: (data: Record<string, unknown>) =>
    api.post(`${LP}/bank-accounts/rules/`, data),
  updateRule: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/bank-accounts/rules/${id}/`, data),
  deleteRule: (id: string) => api.delete(`${LP}/bank-accounts/rules/${id}/`),
}

export const transactionApi = {
  list: (clientId: string, params?: Record<string, string>) =>
    api.get(`${LP}/transactions/`, { params: { client_ledger_id: clientId, ...params } }),
  create: (data: Record<string, unknown>) =>
    api.post(`${LP}/transactions/`, data),
  get: (id: string) => api.get(`${LP}/transactions/${id}/`),
  update: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/transactions/${id}/`, data),
  delete: (id: string) => api.delete(`${LP}/transactions/${id}/`),
  listJournals: (clientId: string) =>
    api.get(`${LP}/transactions/journals/`, { params: { client_ledger_id: clientId } }),
  createJournal: (data: Record<string, unknown>) =>
    api.post(`${LP}/transactions/journals/`, data),
}

export const reportApi = {
  trialBalance: (clientId: string, asAt?: string) =>
    api.get(`${LP}/reports/trial-balance/`, { params: { client_ledger_id: clientId, as_at: asAt } }),
  profitLoss: (clientId: string, startDate?: string, endDate?: string) =>
    api.get(`${LP}/reports/profit-loss/`, { params: { client_ledger_id: clientId, start_date: startDate, end_date: endDate } }),
  balanceSheet: (clientId: string, asAt?: string) =>
    api.get(`${LP}/reports/balance-sheet/`, { params: { client_ledger_id: clientId, as_at: asAt } }),
  cashSummary: (clientId: string, startDate?: string, endDate?: string) =>
    api.get(`${LP}/reports/cash-summary/`, { params: { client_ledger_id: clientId, start_date: startDate, end_date: endDate } }),
  accountDrilldown: (accountId: string, startDate?: string, endDate?: string) =>
    api.get(`${LP}/reports/account/${accountId}/`, { params: { start_date: startDate, end_date: endDate } }),
  exportPdf: (clientId: string, reportType: string, params: Record<string, string>) =>
    api.get(`${LP}/reports/export/`, {
      params: { client_ledger_id: clientId, report_type: reportType, ...params },
      responseType: 'blob',
    }),
}

export const dashboardApi = {
  overview: (firmId?: string) =>
    api.get(`${LP}/dashboard/overview/`, { params: firmId ? { firm_id: firmId } : {} }),
}

export const staffApi = {
  list: (firmId: string) =>
    api.get(`${LP}/staff/`, { params: { firm_id: firmId } }),
  invite: (firmId: string, data: { email: string; role: string }) =>
    api.post(`${LP}/staff/invite/`, data, { params: { firm_id: firmId } }),
  updateRole: (firmId: string, membershipId: string, role: string) =>
    api.patch(`${LP}/staff/${membershipId}/role/`, { role }, { params: { firm_id: firmId } }),
  remove: (firmId: string, membershipId: string) =>
    api.delete(`${LP}/staff/${membershipId}/`, { params: { firm_id: firmId } }),
  cancelInvite: (firmId: string, membershipId: string) =>
    api.delete(`${LP}/staff/${membershipId}/`, { params: { firm_id: firmId } }),
}

export const documentApi = {
  list: (clientId: string, params?: { category?: string; status?: string }) =>
    api.get(`${LP}/documents/${clientId}/`, { params }),
  upload: (
    clientId: string,
    file: File,
    category = '',
    linkedTransactionId?: string,
    linkedInvoiceId?: string,
    linkedBillId?: string,
    linkedQuoteId?: string,
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    if (category) formData.append('category', category)
    if (linkedTransactionId) formData.append('linked_transaction_id', linkedTransactionId)
    if (linkedInvoiceId) formData.append('linked_invoice_id', linkedInvoiceId)
    if (linkedBillId) formData.append('linked_bill_id', linkedBillId)
    if (linkedQuoteId) formData.append('linked_quote_id', linkedQuoteId)
    return api.post(`${LP}/documents/${clientId}/`, formData, {
      headers: { 'Content-Type': null },
    })
  },
  patch: (clientId: string, docId: string, data: Record<string, string | null>) =>
    api.patch(`${LP}/documents/${clientId}/${docId}/`, data),
  delete: (clientId: string, docId: string) =>
    api.delete(`${LP}/documents/${clientId}/${docId}/`),
}

export const budgetApi = {
  list: (clientLedgerId: string, year: number) =>
    api.get(`${LP}/budgets/`, { params: { client_ledger_id: clientLedgerId, year } }),
  bulkUpsert: (data: Record<string, unknown>) =>
    api.post(`${LP}/budgets/bulk-upsert/`, data),
  variance: (clientLedgerId: string, startDate: string, endDate: string) =>
    api.get(`${LP}/budgets/variance/`, {
      params: { client_ledger_id: clientLedgerId, start_date: startDate, end_date: endDate },
    }),
}

export const assetApi = {
  list: (clientLedgerId: string, includeDisposed = false) =>
    api.get(`${LP}/assets/`, { params: { client_ledger_id: clientLedgerId, include_disposed: includeDisposed } }),
  create: (data: Record<string, unknown>) => api.post(`${LP}/assets/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/assets/${id}/`, data),
  runDepreciation: (id: string, data: { date: string; months: number }) =>
    api.post(`${LP}/assets/${id}/run-depreciation/`, data),
  dispose: (id: string, disposedAt: string) =>
    api.post(`${LP}/assets/${id}/dispose/`, null, { params: { disposed_at: disposedAt } }),
}

export const trackingApi = {
  list: (clientLedgerId: string) =>
    api.get(`${LP}/tracking/`, { params: { client_ledger_id: clientLedgerId } }),
  createCategory: (data: Record<string, unknown>) =>
    api.post(`${LP}/tracking/`, data),
  updateCategory: (id: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/tracking/${id}/`, data),
  createOption: (categoryId: string, data: { name: string }) =>
    api.post(`${LP}/tracking/${categoryId}/options/`, data),
  updateOption: (optionId: string, data: Record<string, unknown>) =>
    api.patch(`${LP}/tracking/options/${optionId}/`, data),
  deleteOption: (optionId: string) =>
    api.delete(`${LP}/tracking/options/${optionId}/`),
}

export const billingApi = {
  status: (firmId?: string) =>
    api.get(`${LP}/billing/status/`, { params: firmId ? { firm_id: firmId } : {} }),
  // Checkout sets up a subscription priced on current client counts (no plan arg — per-client billing)
  checkout: (firmId?: string) =>
    api.post(`${LP}/billing/checkout/`, null, { params: firmId ? { firm_id: firmId } : {} }),
  portal: (firmId?: string) =>
    api.post(`${LP}/billing/portal/`, null, { params: firmId ? { firm_id: firmId } : {} }),
}

export const inviteApi = {
  getInfo: (token: string) =>
    api.get(`${LP}/invites/${token}/`),
  accept: (token: string, data: { full_name?: string; password: string }) =>
    api.post(`${LP}/invites/${token}/accept/`, data),
}



export const auditLogApi = {
  list: (params: { client_ledger_id?: string; firm_id?: string; limit?: number; offset?: number }) =>
    api.get(`${LP}/audit-logs/`, { params }),
}

export const aggregateApi = {
  profitLoss: (firmId: string, startDate?: string, endDate?: string) =>
    api.get(`${LP}/aggregate/profit-loss/`, {
      params: { firm_id: firmId, start_date: startDate, end_date: endDate },
    }),
  balanceSheet: (firmId: string, asAt?: string) =>
    api.get(`${LP}/aggregate/balance-sheet/`, {
      params: { firm_id: firmId, as_at: asAt },
    }),
}

export const invoiceApi = {
  list: (clientId: string, status?: string) =>
    api.get(`${LP}/invoices/`, { params: { client_ledger_id: clientId, ...(status ? { status } : {}) } }),
  create: (data: Record<string, unknown>) => api.post(`${LP}/invoices/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/invoices/${id}/`, data),
  send: (id: string) => api.post(`${LP}/invoices/${id}/send/`),
  recordPayment: (id: string, data: { payment_date: string; amount: string; payment_account_id?: string }) =>
    api.post(`${LP}/invoices/${id}/record-payment/`, data),
  void: (id: string) => api.post(`${LP}/invoices/${id}/void/`),
  delete: (id: string) => api.delete(`${LP}/invoices/${id}/`),
}

export const billApi = {
  list: (clientId: string, status?: string) =>
    api.get(`${LP}/bills/`, { params: { client_ledger_id: clientId, ...(status ? { status } : {}) } }),
  create: (data: Record<string, unknown>) => api.post(`${LP}/bills/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/bills/${id}/`, data),
  approve: (id: string) => api.post(`${LP}/bills/${id}/approve/`),
  recordPayment: (id: string, data: { payment_date: string; amount: string; payment_account_id?: string }) =>
    api.post(`${LP}/bills/${id}/record-payment/`, data),
  void: (id: string) => api.post(`${LP}/bills/${id}/void/`),
  delete: (id: string) => api.delete(`${LP}/bills/${id}/`),
}

export const purchaseOrderApi = {
  list: (clientId: string, status?: string) =>
    api.get(`${LP}/purchase-orders/`, { params: { client_ledger_id: clientId, ...(status ? { status } : {}) } }),
  get: (id: string) => api.get(`${LP}/purchase-orders/${id}/`),
  create: (data: Record<string, unknown>) => api.post(`${LP}/purchase-orders/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/purchase-orders/${id}/`, data),
  convertToBill: (id: string, lineQuantities?: Record<string, string>) =>
    api.post(`${LP}/purchase-orders/${id}/convert-to-bill/`, lineQuantities ? { line_quantities: lineQuantities } : {}),
  copy: (id: string) => api.post(`${LP}/purchase-orders/${id}/copy/`),
  send: (id: string, message = '') => api.post(`${LP}/purchase-orders/${id}/send/`, { message }),
  markSent: (id: string) => api.post(`${LP}/purchase-orders/${id}/mark-sent/`),
  approve: (id: string) => api.post(`${LP}/purchase-orders/${id}/approve/`),
  void: (id: string) => api.post(`${LP}/purchase-orders/${id}/void/`),
  delete: (id: string) => api.delete(`${LP}/purchase-orders/${id}/`),
}

export const quoteApi = {
  list: (clientId: string, status?: string) =>
    api.get(`${LP}/quotes/`, { params: { client_ledger_id: clientId, ...(status ? { status } : {}) } }),
  get: (id: string) => api.get(`${LP}/quotes/${id}/`),
  create: (data: Record<string, unknown>) => api.post(`${LP}/quotes/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/quotes/${id}/`, data),
  send: (id: string) => api.post(`${LP}/quotes/${id}/send/`),
  accept: (id: string) => api.post(`${LP}/quotes/${id}/accept/`),
  decline: (id: string) => api.post(`${LP}/quotes/${id}/decline/`),
  convertToInvoice: (id: string) => api.post(`${LP}/quotes/${id}/convert-to-invoice/`),
  copy: (id: string) => api.post(`${LP}/quotes/${id}/copy/`),
  void: (id: string) => api.post(`${LP}/quotes/${id}/void/`),
  delete: (id: string) => api.delete(`${LP}/quotes/${id}/`),
}

export const contactApi = {
  list: (clientId: string, params?: { type_filter?: string; include_archived?: boolean }) =>
    api.get(`${LP}/contacts/`, { params: { client_ledger_id: clientId, ...params } }),
  get: (id: string) => api.get(`${LP}/contacts/${id}/`),
  create: (data: Record<string, unknown>) => api.post(`${LP}/contacts/`, data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`${LP}/contacts/${id}/`, data),
  archive: (id: string) => api.post(`${LP}/contacts/${id}/archive/`),
  restore: (id: string) => api.post(`${LP}/contacts/${id}/restore/`),
}
